import { type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/**
 * Header widgets for a polled operations page: the relative "updated Ns ago"
 * caption, the manual refresh button, and the live/paused polling toggle.
 * They only know about timestamps and callbacks — the page owns the queries.
 */

// UpdatedAgo shows a relative "updated Ns ago" that ticks every second off the
// most recent dataUpdatedAt timestamp the page hands it.
export function UpdatedAgo({ updatedAt }: { updatedAt: number }): JSX.Element | null {
  const { t } = useTranslation('default');
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  if (!updatedAt) return null;
  const seconds = Math.max(0, Math.floor((now - updatedAt) / 1000));
  return (
    <span className="text-[11px] text-text-4 tabular-nums">
      {seconds < 2 ? t('health_updated_just_now') : t('health_updated_ago', { seconds })}
    </span>
  );
}

export function RefreshButton({ fetching, onClick }: { fetching: boolean; onClick: () => void }): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={fetching}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium border transition-colors',
        'border-line text-text-3 hover:text-text hover:bg-surface-2 disabled:opacity-50',
      )}
    >
      {t('health_refresh')}
    </button>
  );
}

export function LiveToggle({ on, fetching, onToggle }: { on: boolean; fetching: boolean; onToggle: () => void }): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium border transition-colors',
        on ? 'border-success/40 text-success hover:bg-success/10' : 'border-line text-text-4 hover:text-text',
      )}
      aria-pressed={on}
      title={t('health_auto_refresh')}
    >
      <span
        className={cn('inline-block w-2 h-2 rounded-full', on ? 'bg-success' : 'bg-text-4', on && fetching && 'animate-pulse')}
        aria-hidden
      />
      {on ? t('vops_live_on') : t('vops_live_paused')}
    </button>
  );
}
